import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from '../../firebase.config';
import { formatCurrency, printReportWithLetterhead, CollapsibleQuickTotals } from './CommonComponents';
import GlobalExportButtons from '../GlobalExportButtons';
import { getCollectionForCompany } from '../../utils/dataAdapter';

const StockReport = ({ db, userId, appId, companyId, dateRange, setLoading, companyDetails }) => {
  const [stockData, setStockData] = useState([]);
  const [totalSummary, setTotalSummary] = useState({
    totalItems: 0,
    negativeStock: 0,
    lowStock: 0
  });
  const [showQuickSummary, setShowQuickSummary] = useState(false);
  const [quickSummaryItem, setQuickSummaryItem] = useState(null);

  // ESC key handler for closing modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showQuickSummary) {
          console.log('ESC: Closing Quick Summary modal');
          setShowQuickSummary(false);
          setQuickSummaryItem(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showQuickSummary]);

  // Handle item click for quick summary
  const handleItemClick = (item) => {
    setQuickSummaryItem(item);
    setShowQuickSummary(true);
  };

  useEffect(() => {
    if (!db || !userId || !companyId) return;
    setLoading(true);

    // Compute quantity from a row (supports nos x length x height pattern)
    const computeRowQty = (row) => {
      const direct = parseFloat(row?.qty);
      if (!Number.isNaN(direct) && direct) return direct;
      return (parseFloat(row?.nos) || 0) * (parseFloat(row?.length) || 1) * (parseFloat(row?.height) || 1);
    };

    const fetchData = async () => {
      try {
        // Date strings for filtering
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];

        // Fetch items using new data adapter
        const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
        if (!itemsCollectionRef) {
          console.warn('Items collection not available');
          setStockData([]);
          setLoading(false);
          return;
        }
        
        const itemsQueryRef = query(itemsCollectionRef, orderBy('itemName', 'asc'));
        const itemsSnapshot = await getDocs(itemsQueryRef);
        const items = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch purchases in date range using new data adapter
        const purchasesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
        let purchases = [];
        if (purchasesCollectionRef) {
          const purchasesQueryRef = query(
            purchasesCollectionRef,
            where('billDate', '>=', startStr),
            where('billDate', '<=', endStr),
            orderBy('billDate', 'asc')
          );
          const purchasesSnapshot = await getDocs(purchasesQueryRef);
          purchases = purchasesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // Fetch sales in date range using new data adapter
        const salesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
        let sales = [];
        if (salesCollectionRef) {
          const salesQueryRef = query(
            salesCollectionRef,
            where('invoiceDate', '>=', startStr),
            where('invoiceDate', '<=', endStr),
            orderBy('invoiceDate', 'asc')
          );
          const salesSnapshot = await getDocs(salesQueryRef);
          sales = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // 🔥 CALC OPTIMIZATION: Optimized O(n) stock calculation (was O(n²))
        console.time('⚡ Stock Calculation');
        
        const startDateStr = new Date(dateRange.start).toISOString().split('T')[0];
        
        // Step 1: Build item transactions map O(bills)
        const itemTransactions = new Map();
        
        // Process purchases
        purchases.forEach(purchase => {
          const rows = Array.isArray(purchase.rows) ? purchase.rows : (Array.isArray(purchase.items) ? purchase.items : []);
          const purchaseDate = purchase.billDate || purchase.date || '';
          
          rows.forEach(row => {
            const itemId = row.item;
            if (!itemId) return;
            
            if (!itemTransactions.has(itemId)) {
              itemTransactions.set(itemId, {
                purchased: 0,
                sold: 0,
                purchasedBefore: 0,
                soldBefore: 0
              });
            }
            
            const qty = computeRowQty(row);
            const trans = itemTransactions.get(itemId);
            trans.purchased += qty;
            
            // Track before opening date
            if (purchaseDate < startDateStr) {
              trans.purchasedBefore += qty;
            }
          });
        });
        
        // Process sales
        sales.forEach(sale => {
          const rows = Array.isArray(sale.rows) ? sale.rows : (Array.isArray(sale.items) ? sale.items : []);
          const saleDate = sale.invoiceDate || sale.date || '';
          const isReturn = sale.docType === 'salesReturn' || sale.isReturn;
          
          rows.forEach(row => {
            const itemId = row.item;
            if (!itemId) return;
            
            if (!itemTransactions.has(itemId)) {
              itemTransactions.set(itemId, {
                purchased: 0,
                sold: 0,
                purchasedBefore: 0,
                soldBefore: 0
              });
            }
            
            const qty = computeRowQty(row);
            const trans = itemTransactions.get(itemId);
            // Sales returns add stock back, regular sales reduce stock
            if (isReturn) {
              trans.sold -= qty; // Subtract from sold (effectively adding stock)
            } else {
              trans.sold += qty;
            }
            
            if (saleDate < startDateStr) {
              if (isReturn) {
                trans.soldBefore -= qty;
              } else {
                trans.soldBefore += qty;
              }
            }
          });
        });

        // Process purchase returns
        const purchaseReturnsSnapshot = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseReturns'));
        const purchaseReturns = purchaseReturnsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(pr => {
          const dt = new Date(pr.billDate || pr.date);
          return (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
        });

        purchaseReturns.forEach(purchaseReturn => {
          const rows = Array.isArray(purchaseReturn.rows) ? purchaseReturn.rows : (Array.isArray(purchaseReturn.items) ? purchaseReturn.items : []);
          const returnDate = purchaseReturn.billDate || purchaseReturn.date || '';
          
          rows.forEach(row => {
            const itemId = row.item;
            if (!itemId) return;
            
            if (!itemTransactions.has(itemId)) {
              itemTransactions.set(itemId, {
                purchased: 0,
                sold: 0,
                purchasedBefore: 0,
                soldBefore: 0
              });
            }
            
            const qty = computeRowQty(row);
            const trans = itemTransactions.get(itemId);
            // Purchase returns reduce stock (subtract from purchased)
            trans.purchased -= qty;
            
            if (returnDate < startDateStr) {
              trans.purchasedBefore -= qty;
            }
          });
        });
        
        // Step 2: Calculate stock for each item O(items)
        const stockReport = items.map(item => {
          const trans = itemTransactions.get(item.id) || {
            purchased: 0,
            sold: 0,
            purchasedBefore: 0,
            soldBefore: 0
          };
          
          const openingStock = (parseFloat(item.openingStock || 0) || 0) + trans.purchasedBefore - trans.soldBefore;
          const inHand = openingStock + trans.purchased - trans.sold;

          let status = 'Normal';
          if (inHand < 0) status = 'Negative';
          else if (inHand <= (item.reorderLevel || 10)) status = 'Low Stock';

          return {
            itemId: item.id,
            itemName: item.itemName,
            openingStock: openingStock,
            purchased: trans.purchased,
            sold: trans.sold,
            inHand: inHand,
            reorderLevel: item.reorderLevel || 10,
            status: status
          };
        });
        
        console.timeEnd('⚡ Stock Calculation');
        console.log(`✅ CALC OPTIMIZATION: Stock report processed ${items.length} items, ${purchases.length} purchases, ${sales.length} sales`);

        setStockData(stockReport);

        const totals = stockReport.reduce((acc, item) => ({
          totalItems: acc.totalItems + 1,
          negativeStock: acc.negativeStock + (item.inHand < 0 ? 1 : 0),
          lowStock: acc.lowStock + (item.status === 'Low Stock' ? 1 : 0)
        }), {
          totalItems: 0,
          negativeStock: 0,
          lowStock: 0
        });
        setTotalSummary(totals);
      } catch (error) {
        console.error('Error fetching stock report data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, userId, appId, companyId, dateRange, setLoading]);

  // Table columns
  const columns = [
    { key: 'itemName', label: 'Item Name' },
    { key: 'openingStock', label: 'Opening Stock' },
    { key: 'purchased', label: 'Purchased' },
    { key: 'sold', label: 'Sold' },
    { key: 'inHand', label: 'In Hand' },
    { key: 'reorderLevel', label: 'Reorder Level' },
    { key: 'status', label: 'Status' }
  ];

  // Prepare export data for GlobalExportButtons
  const getExportData = () => stockData;

  const getExportColumns = () => [
    { key: 'itemName', label: 'Item Name' },
    { key: 'openingStock', label: 'Opening Stock' },
    { key: 'purchased', label: 'Purchased' },
    { key: 'sold', label: 'Sold' },
    { key: 'inHand', label: 'In Hand' },
    { key: 'reorderLevel', label: 'Reorder Level' },
    { key: 'status', label: 'Status' }
  ];

  const getReportDetails = () => ({
    'Period': `${new Date(dateRange.start).toLocaleDateString('en-IN')} to ${new Date(dateRange.end).toLocaleDateString('en-IN')}`,
    'Total Items': totalSummary.totalItems,
    'Negative Stock': totalSummary.negativeStock,
    'Low Stock': totalSummary.lowStock,
    dateRange
  });



  const printReport = () => {
    const columns = [
      { key: 'itemName', label: 'Item Name' },
      { key: 'openingStock', label: 'Opening Stock' },
      { key: 'purchased', label: 'Purchased' },
      { key: 'sold', label: 'Sold' },
      { key: 'inHand', label: 'In Hand', 
        render: (value, row) => `<span class="${value < 0 ? 'text-red-600' : value > 0 ? 'text-green-600' : ''}">${value || 0}</span>` },
      { key: 'reorderLevel', label: 'Reorder Level' },
      { key: 'status', label: 'Status',
        render: (value) => `<span class="${value === 'Normal' ? 'text-green-600' : value === 'Low Stock' ? 'text-yellow-600' : 'text-red-600'}">${value || ''}</span>` }
    ];

    const additionalInfo = {
      'Period': `${new Date(dateRange.start).toLocaleDateString('en-IN')} to ${new Date(dateRange.end).toLocaleDateString('en-IN')}`,
      'Total Items': totalSummary.totalItems,
      'Negative Stock': totalSummary.negativeStock,
      'Low Stock': totalSummary.lowStock
    };

    printReportWithLetterhead('Stock Report', stockData, columns, companyDetails, additionalInfo);
  };



  return (
    <div className="p-3 sm:p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Stock Report (Auto Calculated)</h2>
            <p className="text-gray-600">
              Period: {new Date(dateRange.start).toLocaleDateString('en-IN')} to {new Date(dateRange.end).toLocaleDateString('en-IN')}
            </p>
          </div>
          {/* Global Export/Print/Share Buttons */}
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="STOCK"
            title="Stock Report"
            companyDetails={companyDetails}
            reportDetails={getReportDetails()}
            disabled={stockData.length === 0}
          />
        </div>
      </div>
      <CollapsibleQuickTotals
        cards={[
          { label: 'Total Items', value: totalSummary.totalItems, color: 'blue' },
          { label: 'Negative Stock', value: totalSummary.negativeStock, color: 'red' },
          { label: 'Low Stock', value: totalSummary.lowStock, color: 'yellow' }
        ]}
      />
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="md:hidden p-3 space-y-2">
          {stockData.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No data found</div>
          ) : stockData.map((row, idx) => (
            <button
              key={row.itemId || idx}
              type="button"
              onClick={() => handleItemClick(row)}
              className="w-full text-left border border-gray-200 rounded-lg p-3 touch-manipulation active:bg-blue-50"
            >
              <div className="font-medium text-gray-900 text-sm mb-2">{row.itemName}</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                <span>Opening: {row.openingStock}</span>
                <span>Purchased: {row.purchased}</span>
                <span>Sold: {row.sold}</span>
                <span className={row.inHand < 0 ? 'text-red-700 font-semibold' : row.inHand > 0 ? 'text-green-700 font-semibold' : ''}>
                  In hand: {row.inHand}
                </span>
              </div>
              <div className="mt-2 text-xs">
                <span className={`px-2 py-0.5 rounded font-medium ${
                  row.status === 'Normal' ? 'bg-green-100 text-green-800' :
                  row.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>{row.status}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stockData.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-8 text-gray-400">No data found</td></tr>
            ) : stockData.map((row, idx) => (
              <tr key={row.itemId || idx} className="cursor-pointer hover:bg-blue-50" onClick={() => handleItemClick(row)}>
                {columns.map(col => {
                  const isInHand = col.key === 'inHand';
                  const cls = isInHand
                    ? (row.inHand < 0 ? 'text-red-700' : row.inHand > 0 ? 'text-green-700' : 'text-gray-700')
                    : '';
                  return (
                    <td key={col.key} className={`px-6 py-4 whitespace-nowrap ${cls}`}>
                      {row[col.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      
      {/* Totals */}
      <div className="mt-3 text-sm text-gray-700">
        <div><strong>Total Items:</strong> {totalSummary.totalItems}</div>
      </div>
      {/* Quick Summary Modal */}
      {showQuickSummary && quickSummaryItem && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md relative max-h-[92dvh] overflow-y-auto">
            <button className="absolute top-2 right-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 text-xl touch-manipulation" onClick={() => setShowQuickSummary(false)} aria-label="Close">&times;</button>
            <h3 className="text-lg font-bold mb-4 text-center">Stock Item Summary</h3>
            <div className="space-y-3">
              <div><strong>Item Name:</strong> {quickSummaryItem.itemName}</div>
              <div><strong>Item ID:</strong> {quickSummaryItem.itemId}</div>
              <div><strong>Opening Stock:</strong> {quickSummaryItem.openingStock}</div>
              <div><strong>Purchased:</strong> {quickSummaryItem.purchased}</div>
              <div><strong>Sold:</strong> {quickSummaryItem.sold}</div>
              <div><strong>In Hand:</strong> {quickSummaryItem.inHand}</div>
              <div><strong>Reorder Level:</strong> {quickSummaryItem.reorderLevel}</div>
              <div><strong>Status:</strong> 
                <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                  quickSummaryItem.status === 'Normal' ? 'bg-green-100 text-green-800' :
                  quickSummaryItem.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {quickSummaryItem.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReport; 